import {
  Button,
  Centralizer,
  Col,
  Divider,
  Form,
  LinkText,
  PasswordField,
  Row,
  SubmitButton,
  Text,
  TextInputField,
} from '@/components';
import { VITE_API_URL } from '@/config';
import { useChangeBrowserTabTitleEffect, useForm } from '@/hooks';
import { setUser } from '@/lib/datadog';
import { useMessage } from '@/providers';
import { isPasswordResettable, isProd } from '@/utils';
import { ExportOutlined } from '@ant-design/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useCallback, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import type { z } from 'zod';

import { apiErrorCodeType } from '@/api/error-codes';
import useUmami from '@/lib/umami';
import { isAxiosError } from 'axios';
import { AuthenticationPagesLayout } from '../_components/AuthenticationPagesLayout';
import { useLogin } from '../_hooks/login';
import { LoginFormSchema } from '../_models/login';

type FormValues = z.infer<typeof LoginFormSchema>;

const LoginPage: FC = () => {
  const { mutateAsync: loginAsync } = useLogin();
  const umami = useUmami();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [, , removeCookie] = useCookies();
  const { openMessage } = useMessage();
  const [searchParams] = useSearchParams();

  // TODO: The previous title may be shown when history back called.
  useChangeBrowserTabTitleEffect({ title: t('signin') });

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(LoginFormSchema),
    reValidateMode: 'onSubmit',
  });

  const onSubmit = useCallback(
    async (values: FormValues) => {
      try {
        const response = await loginAsync(values);
        setUser(response.id);
        umami.identify({
          id: response.id,
          name: response.username,
          email: response.email,
        });
        const redirectUri = searchParams.get('redirectUri');
        navigate(redirectUri ?? '/');
      } catch (error) {
        if (isAxiosError(error)) {
          const errorCode = error.response?.data.error_code;
          if (
            errorCode ===
            apiErrorCodeType.Values
              .BNS_API_COMMON_403_0003_LOGIN_TIME_RESTRICTED
          ) {
            const details = error.response?.data.details;
            try {
              const canLoginFrom = details[0].can_login_from;
              const canLoginTo = details[0].can_login_to;
              await openMessage(
                'warning',
                t('warning-message-login-time-restricted', {
                  canLoginFrom: canLoginFrom,
                  canLoginTo: canLoginTo,
                }),
              );
              return;
            } catch {
              await openMessage(
                'warning',
                t('warning-message-login-time-restricted-fallback'),
              );
              return;
            }
          }
        }

        await openMessage('error', t('error-message-login-failed'));
      }
    },
    [loginAsync, navigate, openMessage, searchParams, t, umami],
  );

  useEffect(() => {
    removeCookie('access_token');
    removeCookie('csrftoken');
  }, [removeCookie]);

  return (
    <AuthenticationPagesLayout cardTitle={t('signin')}>
      {/* NOTE: GIMA は本番系のみに接続されているため、検証環境では GIMA 認証を出さない */}
      {isProd() && (
        <>
          <Row gutter={[0, 16]}>
            <Col span={24}>
              <Text text={t('message-login-with-gima')} />
            </Col>
            <Col span={24}>
              <Button
                onClick={async () => {
                  // NOTES:
                  // axios, fetch and even XMLHttpRequest cannot deal with response of 302 (redirect) well.
                  // So, we have to handle it by manual.
                  const res = await fetch(`${VITE_API_URL}/api/gima/login/`, {
                    redirect: 'manual',
                  });
                  if (res.type === 'opaqueredirect') {
                    window.location.href = res.url;
                  } else {
                    return res;
                  }
                }}
              >
                {t('signin-with-gima')}
                <ExportOutlined aria-label="" />
              </Button>
            </Col>
          </Row>
          <Divider>{t('or')}</Divider>
        </>
      )}
      <Form
        leavePageAlertCondition={null}
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
        autoComplete="off"
      >
        <TextInputField
          label={t('email')}
          name="email"
          control={control}
          errors={errors}
        />
        <PasswordField
          label={t('password')}
          name="password"
          control={control}
          errors={errors}
        />

        <Form.Item>
          <Centralizer>
            <SubmitButton loading={isSubmitting} text={t('signin')} />
          </Centralizer>
        </Form.Item>
      </Form>

      {isPasswordResettable() && (
        <Centralizer>
          <LinkText
            onClick={() => {
              navigate('/login/reset-password');
            }}
            text={t('reset-forgot-password')}
          />
        </Centralizer>
      )}
    </AuthenticationPagesLayout>
  );
};

export default LoginPage;
